import tinycolor from 'tinycolor2';
import { createTheme } from '@mantine/core';
import {
  DEFAULT_SITE_FONT, isThemeConfigured, readThemeConfiguration,
} from '../../shared/theme/themeConfiguration';

/**
 * Génère 10 teintes Mantine (shade 0 → 9) depuis une couleur hex de base.
 * Convention Mantine : shade 0 = très clair, shade 9 = très foncé.
 * La couleur d'entrée est placée en shade 6 (valeur principale).
 */
function generateShades(hex) {
  const base = tinycolor(hex);
  const shades = [];

  // Shades 0-5 : de très clair à légèrement clair
  for (let i = 0; i < 6; i++) {
    const lightAmount = (6 - i) * 12;
    shades.push(base.clone().lighten(lightAmount).toHexString());
  }

  // Shade 6 : couleur de base
  shades.push(base.toHexString());

  // Shades 7-9 : de sombre à très sombre
  shades.push(base.clone().darken(10).toHexString());
  shades.push(base.clone().darken(20).toHexString());
  shades.push(base.clone().darken(32).toHexString());

  return shades;
}

/**
 * Construit un thème Mantine complet depuis la config de personnalisation du projet web.
 *
 * @param {{
 *   primaryColor?: string,
 *   fontFamily?: string,
 *   fontSize?: number,
 *   borderRadius?: 'xs'|'sm'|'md'|'lg'|'xl',
 *   defaultRadius?: 'xs'|'sm'|'md'|'lg'|'xl',
 *   configuration?: object,
 * }} themeConfig
 * @returns {import('@mantine/core').MantineThemeOverride}
 */
export function buildMantineTheme(themeConfig = {}) {
  const {
    primaryColor = '#228be6',
    defaultRadius = 'md',
  } = themeConfig;
  // La police du thème configuré (onglet Police du configurateur) prime sur
  // l'ancien réglage.
  const configured = isThemeConfigured(themeConfig) ? readThemeConfiguration(themeConfig) : null;
  const fontFamily = configured?.fontFamily
    ? `${configured.fontFamily}, sans-serif`
    : themeConfig.fontFamily || `${DEFAULT_SITE_FONT}, sans-serif`;
  const fontSize = configured?.fontSize || themeConfig.fontSize || 16;

  const shades = generateShades(primaryColor);

  return createTheme({
    primaryColor: 'brand',
    colors: {
      brand: shades,
    },
    fontFamily,
    headings: {
      fontFamily,
    },
    defaultRadius,
    fontSizes: {
      xs: `${fontSize * 0.75}px`,
      sm: `${fontSize * 0.875}px`,
      md: `${fontSize}px`,
      lg: `${fontSize * 1.125}px`,
      xl: `${fontSize * 1.25}px`,
    },
  });
}

/**
 * Valeurs par défaut de la config de thème pour l'UI d'édition.
 */
export const defaultThemeConfig = {
  primaryColor: '#228be6',
  fontFamily: `${DEFAULT_SITE_FONT}, sans-serif`,
  fontSize: 16,
  defaultRadius: 'md',
  /** Palette nommée du template, `{ name, value }[]` (cf. utils/templatePalette) */
  colors: [],
};
